import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { RealtimeEvents } from '@raahi/shared';
import { apiBaseUrl } from '../lib/env.js';
import { useSession } from '../state/session.js';
import { useOffer, type TripOffer } from '../state/offer.js';
import { IS_DEMO } from '../demo/config.js';
import { createDemoSocket } from '../demo/socket.js';

export function useDriverSocket(): Socket | null {
  const token = useSession((s) => s.accessToken);
  const setOffer = useOffer((s) => s.setOffer);
  const setActiveTripId = useOffer((s) => s.setActiveTripId);
  const ref = useRef<Socket | null>(null);

  useEffect(() => {
    const onOffer = (payload: unknown) => setOffer(payload as TripOffer);
    const onUpdate = (payload: unknown) => {
      const status = (payload as { status?: string } | undefined)?.status;
      if (status === 'completed' || status === 'cancelled') setActiveTripId(null);
    };

    if (IS_DEMO) {
      const socket = createDemoSocket();
      socket.on(RealtimeEvents.TripOffer, onOffer);
      socket.on(RealtimeEvents.TripUpdate, onUpdate);
      ref.current = socket as unknown as Socket;
      return () => {
        socket.off(RealtimeEvents.TripOffer, onOffer);
        socket.off(RealtimeEvents.TripUpdate, onUpdate);
        socket.disconnect();
        ref.current = null;
      };
    }

    if (!token) {
      ref.current?.disconnect();
      ref.current = null;
      return;
    }
    const socket = io(apiBaseUrl, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
    });
    ref.current = socket;

    socket.on(RealtimeEvents.TripOffer, onOffer as (payload: TripOffer) => void);
    socket.on(RealtimeEvents.TripUpdate, onUpdate);
    return () => {
      socket.off(RealtimeEvents.TripOffer, onOffer as (payload: TripOffer) => void);
      socket.off(RealtimeEvents.TripUpdate, onUpdate);
      socket.disconnect();
      ref.current = null;
    };
  }, [token, setOffer, setActiveTripId]);

  return ref.current;
}
